import { CgCustomDomainStatus } from '@provision/landing-page-utils';
import type { CgCustomDomain } from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.types';
import type { LegalDocument } from 'src/graphql/entities/legal-document/legal-document.types';

interface SharedDomainConflict {
  hostname: string;
  brandIds: string[];
}

/** One domain shows one page per path, so segments sharing it with different documents clash. */
export function findSharedDomainConflict(
  domains: CgCustomDomain[],
  documents: LegalDocument[],
  brandId: string,
): SharedDomainConflict | null {
  const domain = domains.find(
    ({ status, brandIds }) =>
      status === CgCustomDomainStatus.Active && brandIds.includes(brandId),
  );

  if (!domain) return null;

  const documentOf = (segmentId: string) =>
    documents.find((document) => document.brandIds.includes(segmentId))?.legalDocumentId;
  const ownDocument = documentOf(brandId);

  const conflicting = domain.brandIds.filter((segmentId) => {
    const other = segmentId === brandId ? undefined : documentOf(segmentId);

    return other !== undefined && other !== ownDocument;
  });

  return conflicting.length ? { hostname: domain.hostname, brandIds: conflicting } : null;
}

export function describeSharedDomainConflict(
  { hostname, brandIds }: SharedDomainConflict,
  segmentName: (brandId: string) => string,
): string {
  const segments = brandIds.map(segmentName).join(', ');

  return `${hostname} also serves ${segments}, which use a different document. Visitors there see the first one found.`;
}
